'use client';

import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import { useHandleSearch } from '@/hooks/useHandleSearch';

interface SearchTermsCarouselProps {
  searchTerms?: string[];
}

const SearchTermsCarousel: React.FC<SearchTermsCarouselProps> = ({ searchTerms }) => {
  const [startIndex, setStartIndex] = useState(0);
  const [visibleCount, setVisibleCount] = useState(0);
  const [userInteracted, setUserInteracted] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { handleSearch } = useHandleSearch();
  const chipWidth = 266;

  const totalSlides = searchTerms?.length || 0;

  const resetTimeout = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      setUserInteracted(false);
    }, 10000);
  };

  useEffect(() => {
    const updateVisibleCount = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth - 76;
        setVisibleCount(Math.max(1, Math.floor(containerWidth / chipWidth)));
      }
    };

    updateVisibleCount();
    window.addEventListener('resize', updateVisibleCount);
    return () => window.removeEventListener('resize', updateVisibleCount);
  }, [chipWidth]);

  useEffect(() => {
    if (!userInteracted && totalSlides > 0) {
      const interval = setInterval(() => {
        setStartIndex((prev) => (prev + visibleCount === totalSlides ? 0 : prev + 1));
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [totalSlides, visibleCount, userInteracted, startIndex]);

  useEffect(() => {
    resetTimeout();
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [userInteracted]);

  const handleNextSlide = () => {
    setStartIndex((prev) => (prev + 1) % totalSlides);
    setUserInteracted(true);
    resetTimeout();
  };

  const handlePrevSlide = () => {
    setStartIndex((prev) => (prev - 1 + totalSlides) % totalSlides);
    setUserInteracted(true);
    resetTimeout();
  };

  if (!searchTerms || searchTerms.length === 0) {
    return null;
  }

  return (
    <div className="relative mx-auto flex w-full items-center" ref={containerRef}>
      <div className="mx-auto flex gap-1 overflow-hidden" style={{ width: visibleCount * chipWidth + 76 }}>
        <button
          onClick={handlePrevSlide}
          disabled={startIndex === 0}
          className="absolute left-0 z-10 flex h-8 w-8 items-center justify-center pt-2 disabled:opacity-50"
        >
          <FontAwesomeIcon icon={faChevronLeft} className="h-6 w-6 text-primary-content" />
        </button>
        <div className="mx-auto flex gap-1 overflow-hidden" style={{ width: visibleCount * chipWidth }}>
          <ul
            className="flex transition-transform duration-300 ease-in-out"
            style={{
              transform: `translateX(-${startIndex * chipWidth}px)`,
              width: totalSlides * chipWidth,
            }}
          >
            {searchTerms.map((item, index) => (
              <li
                key={index}
                onClick={() => handleSearch(item)}
                className="badge-lg mx-2 flex min-h-10 w-[250px] shrink-0 cursor-pointer items-center justify-center whitespace-pre-wrap rounded-full border-2 border-primary-content bg-primary px-6 py-5 text-center text-sm leading-4 text-primary-content opacity-90 transition duration-200 ease-in-out hover:bg-primary-content hover:text-primary"
              >
                {item}
              </li>
            ))}
          </ul>
        </div>
        <button
          onClick={handleNextSlide}
          disabled={startIndex + visibleCount === totalSlides}
          className="absolute right-0 z-10 flex h-8 w-8 items-center justify-center pt-2 disabled:opacity-50"
        >
          <FontAwesomeIcon icon={faChevronRight} className="h-6 w-6 text-primary-content" />
        </button>
      </div>
    </div>
  );
};

export default SearchTermsCarousel;
